/**
 * Whether a store-facing surface is hidden, waiting, locked, or open
 * (MOT-794, flag `show_locked_plan_features`).
 *
 * A rollout flag that is off (default, global, or an organization override)
 * stays hidden. A flag that is off because the plan does not include its
 * `planFeature` (`source === 'plan'`) is a plan lock, same as a nav row whose
 * only gate is the entitlement.
 */

export type PlanSurfaceMode = 'hidden' | 'pending' | 'locked' | 'open';

export interface PlanSurfaceInput {
  feature?: string;
  rank?: { key: string; minimum: 'advanced' | 'complete' };
  orgFlag?: string;
}

export interface PlanRequirementRow {
  included: boolean;
  minimumPlan: string | null;
}

export interface PlanRankRow {
  level: 'basic' | 'advanced' | 'complete';
  plans: { advanced: string | null; complete: string | null };
}

export interface PlanRequirementsPayload {
  planName: string;
  features: Record<string, PlanRequirementRow>;
  ranks: Record<string, PlanRankRow>;
}

export interface PlanSurfaceContext {
  flagsLoading: boolean;
  showLocked: boolean;
  requirements: PlanRequirementsPayload | null;
  requirementsPending: boolean;
  requirementsFailed: boolean;
  orgFlagEnabled: boolean;
  orgFlagSource?: 'default' | 'global' | 'organization' | 'plan';
}

export interface PlanSurfaceResult {
  mode: PlanSurfaceMode;
  planName: string | null;
}

const RANK_ORDER = { basic: 0, advanced: 1, complete: 2 } as const;

function featureDecision(requirements: PlanRequirementsPayload, feature: string): { included: boolean; planName: string | null } {
  const row = requirements.features[feature];
  // An unknown key fails open: a missing row must not lock a feature the
  // server did not describe.
  if (!row) return { included: true, planName: null };
  return { included: row.included === true, planName: row.minimumPlan };
}

function rankDecision(
  requirements: PlanRequirementsPayload,
  rank: { key: string; minimum: 'advanced' | 'complete' },
): { included: boolean; planName: string | null } {
  const row = requirements.ranks[rank.key];
  if (!row || !(row.level in RANK_ORDER)) return { included: true, planName: null };
  return {
    included: RANK_ORDER[row.level] >= RANK_ORDER[rank.minimum],
    planName: row.plans[rank.minimum] ?? null,
  };
}

export function resolvePlanSurface(input: PlanSurfaceInput, ctx: PlanSurfaceContext): PlanSurfaceResult {
  if (ctx.flagsLoading) return { mode: 'pending', planName: null };

  const released = !input.orgFlag || ctx.orgFlagEnabled;
  if (!ctx.showLocked) {
    return { mode: released ? 'open' : 'hidden', planName: null };
  }
  if (ctx.requirementsPending || (!ctx.requirements && !ctx.requirementsFailed)) {
    return { mode: 'pending', planName: null };
  }
  if (ctx.requirementsFailed || !ctx.requirements) {
    return { mode: released ? 'open' : 'hidden', planName: null };
  }

  const decision = input.rank
    ? rankDecision(ctx.requirements, input.rank)
    : input.feature
      ? featureDecision(ctx.requirements, input.feature)
      : { included: true, planName: null };

  // Included, or sold by no tier (nothing honest to name): keep today's visibility.
  if (decision.included || !decision.planName) {
    return { mode: released ? 'open' : 'hidden', planName: null };
  }

  const lockedByPlanFlag = Boolean(input.orgFlag) && !ctx.orgFlagEnabled && ctx.orgFlagSource === 'plan';
  if (released || lockedByPlanFlag) {
    return { mode: 'locked', planName: decision.planName };
  }
  return { mode: 'hidden', planName: null };
}

/** Plan name when the nav row should render locked; null when it follows today's visibility. */
export function navLeafLockName(
  leaf: { planFeature?: string; orgFlag?: string },
  ctx: PlanSurfaceContext,
): string | null {
  if (!leaf.planFeature) return null;
  const resolved = resolvePlanSurface({ feature: leaf.planFeature, orgFlag: leaf.orgFlag }, ctx);
  return resolved.mode === 'locked' ? resolved.planName ?? '' : null;
}
